import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, parseISO } from 'date-fns';
import { capitalize, isEmpty } from 'lodash';
import * as React from 'react';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { handleEnterAsTab } from '../../utils/handleEnterAsTab';
import { BoxWithHeader, TransparentChip } from '../common';
import { Accident, CarModelsList, TotalCrashType } from '../types/accident';
import { AccidentOrvScanFields } from './AccidentOrvScanFields';
import { useAccident } from './hooks/useAccident';
import { isStkExpired } from './utils/utils';

interface AccidentCreatingStepInformationsProps {
  onSubmit: (data: Accident) => void;
  formRef: React.RefObject<HTMLFormElement>;
  accident?: Accident;
  carBrandsAndModels?: CarModelsList[];
}

export const AccidentCreatingStepCarInformations = ({
  onSubmit,
  formRef,
  accident,
  carBrandsAndModels
}: AccidentCreatingStepInformationsProps) => {
  const { handleScanTP, openScanner, setOpenScanner, carData, isLoadingCarData, handleGetCarData, orv, setOrv } =
    useAccident();

  const methods = useForm<Accident>({
    defaultValues: {
      car: {
        ...accident?.car,
        isTotalCrash: TotalCrashType.NOT_TOTAL_CRASH,
        speedometerValue: 'Není známý',
        tankValue: 'Neznámá',
        brand: accident?.car?.brand ?? '',
        model: accident?.car?.model ?? '',
        plateNumber: accident?.car?.plateNumber ?? '',
        vin: accident?.car?.vin ?? '',
        manufacturedYear: accident?.car?.manufacturedYear ?? '',
        stk: accident?.car?.stk,
        wasDamagedBefore: accident?.car?.wasDamagedBefore ?? 0,
        hasLeasing: accident?.car?.hasLeasing ?? 0,
        leasingNumber: accident?.car?.leasingNumber ?? '',
        location: accident?.car?.location ?? '',
        contactPerson: accident?.car?.contactPerson ?? ''
      }
    }
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue
  } = methods;

  const hasLeasing = useWatch({
    control,
    name: 'car.hasLeasing'
  });

  useEffect(() => {
    if (accident && !isEmpty(accident)) {
      reset(accident);
      setOrv(accident.car?.licenseId ?? '');
    }

    if (carData && !isEmpty(carData.response)) {
      reset({
        car: {
          brand: capitalize(carData?.response?.TovarniZnacka),
          model: capitalize(carData?.response?.ObchodniOznaceni),
          manufacturedYear: carData.response.DatumPrvniRegistrace,
          stk: carData.response.PravidelnaTechnickaProhlidkaDo,
          vin: carData?.response.VIN,
          licenseId: orv
        }
      });
    }
  }, [accident, carData]);

  useEffect(() => {
    setValue('car.licenseId', orv);
  }, [orv, setValue]);

  useEffect(() => {
    if (!hasLeasing) {
      setValue('car.leasingNumber', '');
    }
  }, [hasLeasing, setValue]);

  const selectedBrand = watch('car.brand');
  const availableModels = carBrandsAndModels?.find((brand) => brand.title === selectedBrand)?.models || [];

  const stkValue = watch('car.stk');
  const stkExpired = isStkExpired(stkValue, accident?.date);

  return (
    <BoxWithHeader title="Údaje o vozidle" mt={2} style={{ height: 'auto' }}>
      <AccidentOrvScanFields
        technicalIdNumber={orv}
        onTechnicalIdChange={(value) => setOrv(value)}
        closeScannerClick={() => setOpenScanner(false)}
        openScanner={openScanner}
        openScannerClick={() => setOpenScanner(true)}
        onGetCarClick={handleGetCarData}
        isLoadingCarData={isLoadingCarData}
        onScan={(value) => handleScanTP(value)}
      />
      <Box mb={3} mt={1}>
        <Divider />
      </Box>
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <Grid container spacing={2} mt={1}>
          <Grid size={12}>
            <Stack gap={2}>
              <Controller
                control={control}
                name="car.brand"
                render={({ field }) => {
                  const [inputValue, setInputValue] = React.useState(field.value || '');

                  React.useEffect(() => {
                    setInputValue(field.value || '');
                  }, [field.value]);

                  return (
                    <Autocomplete
                      disablePortal
                      freeSolo
                      value={field.value || ''}
                      inputValue={inputValue}
                      onInputChange={(_, newValue) => {
                        setInputValue(newValue);
                        field.onChange(newValue);
                      }}
                      options={Array.isArray(carBrandsAndModels) ? carBrandsAndModels.map((item) => item.title) : []}
                      onChange={(_, newValue) => {
                        field.onChange(newValue);
                        setInputValue(newValue || '');
                        // Reset model when brand changes
                        const updatedModels = Array.isArray(carBrandsAndModels)
                          ? carBrandsAndModels.find((brand) => brand.title === newValue)?.models || []
                          : [];
                        setValue('car.model', updatedModels[0]?.title || '', {
                          shouldValidate: true
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Značka vozu"
                          error={!!errors.car?.brand}
                          helperText={errors.car?.brand?.message}
                          fullWidth
                        />
                      )}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="car.model"
                render={({ field }) => {
                  const [inputValue, setInputValue] = React.useState(field.value || '');

                  React.useEffect(() => {
                    setInputValue(field.value || '');
                  }, [field.value]);

                  return (
                    <Autocomplete
                      disablePortal
                      freeSolo
                      value={field.value || ''}
                      inputValue={inputValue}
                      onInputChange={(_, newValue) => {
                        setInputValue(newValue);
                        field.onChange(newValue);
                      }}
                      options={availableModels?.map((item) => item.title) || []}
                      onChange={(_, newValue) => {
                        field.onChange(newValue);
                        setInputValue(newValue || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Model vozu"
                          error={!!errors.car?.model}
                          helperText={errors.car?.model?.message}
                          fullWidth
                        />
                      )}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="car.plateNumber"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChange={(e) => onChange(e.target.value.toUpperCase())}
                    label="SPZ"
                    color="secondary"
                    fullWidth
                    required
                    error={Boolean(errors.car?.plateNumber)}
                    helperText={errors.car?.plateNumber?.message}
                    InputLabelProps={{ shrink: true }}
                    onKeyDown={handleEnterAsTab}
                  />
                )}
              />
              <Controller
                control={control}
                name="car.vin"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="VIN"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.car?.vin)}
                    helperText={errors.car?.vin?.message}
                    InputLabelProps={{ shrink: true }}
                    onKeyDown={handleEnterAsTab}
                  />
                )}
              />
              <Box>
                <Controller
                  control={control}
                  name="car.manufacturedYear"
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      label="Rok výroby *"
                      value={value ? parseISO(value) : null}
                      onChange={(date) => onChange(date ? format(date, 'yyyy') : '')}
                      sx={{ width: 1 }}
                      format="yyyy"
                      views={['year']}
                      maxDate={new Date()}
                      slotProps={{
                        textField: {
                          error: Boolean(errors.car?.manufacturedYear),
                          helperText: errors.car?.manufacturedYear?.message
                        }
                      }}
                    />
                  )}
                />
                {errors.car?.manufacturedYear?.message && (
                  <Typography variant="body2" color="error">
                    {errors.car?.manufacturedYear?.message}
                  </Typography>
                )}
              </Box>
              <Box>
                <Controller
                  control={control}
                  name="car.stk"
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      label="Platnost STK"
                      value={value ? new Date(value) : null}
                      onChange={onChange}
                      sx={{ width: 1 }}
                      slotProps={{
                        textField: {
                          error: Boolean(errors.car?.stk),
                          helperText: errors.car?.stk?.message
                        }
                      }}
                    />
                  )}
                />
                {stkExpired && (
                  <Box mt={1}>
                    <TransparentChip label="STK je prošlá" color="error" variant="filled" />
                  </Box>
                )}
              </Box>
              <Controller
                control={control}
                name="car.speedometerValue"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Stav Km"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.car?.speedometerValue)}
                    helperText={errors.car?.speedometerValue?.message}
                    InputLabelProps={{ shrink: true }}
                    onKeyDown={handleEnterAsTab}
                  />
                )}
              />
              <FormControl fullWidth>
                <InputLabel
                  id="tankvalue-label"
                  sx={{
                    backgroundColor: 'white',
                    px: 0.5
                  }}
                >
                  Stav nádrže u přebírání auta
                </InputLabel>
                <Controller
                  control={control}
                  name="car.tankValue"
                  render={({ field: { onChange, value } }) => (
                    <Select value={value} onChange={onChange} labelId="tankvalue-label">
                      <MenuItem value="Neznámá">Neznámá</MenuItem>
                      <MenuItem value="Prázdná">Prázdná</MenuItem>
                      <MenuItem value="Čtvrt">Čtvrt</MenuItem>
                      <MenuItem value="Půl">Půl</MenuItem>
                      <MenuItem value="Tři čtvrtě">Tři čtvrtě</MenuItem>
                      <MenuItem value="Plná">Plná</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel id="totalCrash-label">Totální škoda</FormLabel>
                <Controller
                  control={control}
                  name="car.isTotalCrash"
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup
                      aria-labelledby="totalCrash-label"
                      name="radio-buttons-group"
                      value={value}
                      onChange={onChange}
                    >
                      <FormControlLabel value="0" control={<Radio />} label="Není totální škoda" />
                      <FormControlLabel value="1" control={<Radio />} label="Je totální škoda" />
                      <FormControlLabel value="2" control={<Radio />} label="Možná totální škoda" />
                    </RadioGroup>
                  )}
                />
              </FormControl>

              <Controller
                control={control}
                name="car.wasDamagedBefore"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} />
                    }
                    label="Bylo poškozeno před dopravní nehodou"
                  />
                )}
              />

              <Controller
                control={control}
                name="car.hasLeasing"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} />
                    }
                    label="Je vozidlo na leasing"
                  />
                )}
              />
              {Boolean(hasLeasing) && (
                <Controller
                  control={control}
                  name="car.leasingNumber"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Číslo leasingové / úvěrové smlouvy"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.car?.leasingNumber)}
                      helperText={errors.car?.leasingNumber?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
              )}
              <Controller
                control={control}
                name="car.location"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Místo, kde se automobil nachází"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.car?.location)}
                    helperText={errors.car?.location?.message}
                    InputLabelProps={{ shrink: true }}
                    onKeyDown={handleEnterAsTab}
                  />
                )}
              />
              <Controller
                control={control}
                name="car.contactPerson"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Kontaktní osoba v případě prohlídky"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.car?.contactPerson)}
                    helperText={errors.car?.contactPerson?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Stack>
          </Grid>
        </Grid>
      </form>
    </BoxWithHeader>
  );
};
